import React, { useEffect, useState } from "react";
import { Modal, Form, Select, Progress, message, Row, Col, Spin } from "antd";
import useFileDownload from "../../../hooks/useFileDownload";
import { getClientConfig, isEmpty } from "../../../generalFunctions";

function BulkQRDownload({ visible, close }) {
  const [form] = Form.useForm();

  // Use the new useFileDownload hook instead of useApi
  const [downloadState, downloadFile] = useFileDownload(
    "/config/user/bulk-qr-download",
    "POST"
  );

  const [selectedCompany, setSelectedCompany] = useState(null);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [downloadProgress, setDownloadProgress] = useState(0);

  const { company, project, ward } = getClientConfig();

  // Handle successful download
  useEffect(() => {
    if (!downloadState.isLoading && downloadState.progress === 100) {
      message.success("QR codes downloaded successfully!");
      setDownloadProgress(0);
      handleCancel();
    }
  }, [downloadState.isLoading, downloadState.progress]);

  // Simulate progress while loading
  useEffect(() => {
    if (downloadState.isLoading) {
      // Reset progress when starting
      setDownloadProgress(0);

      const interval = setInterval(() => {
        setDownloadProgress((prev) => {
          if (prev < 90) {
            return prev + Math.random() * 30;
          }
          return prev;
        });
      }, 500);

      return () => clearInterval(interval);
    } else {
      // When download completes, jump to 100%
      if (downloadState.progress === 100) {
        setDownloadProgress(100);
      }
    }
  }, [downloadState.isLoading, downloadState.progress]);

  const handleDownload = () => {
    form
      .validateFields()
      .then(async (values) => {
        // Generate filename with timestamp
        const timestamp = new Date().getTime();
        const filename = `qr-codes-${timestamp}.pdf`;

        // Call the download function
        const success = await downloadFile(values, filename);

        // If download failed, reset progress
        if (!success) {
          setDownloadProgress(0);
        }
      })
      .catch((error) => {
        console.log(error);
        message.error("Please fill all required fields");
      });
  };

  const handleCancel = () => {
    form.resetFields();
    setSelectedCompany(null);
    setFilteredProjects([]);
    setDownloadProgress(0);
    close();
  };

  const companySelected = (value) => {
    setSelectedCompany(value);
    setFilteredProjects(
      !isEmpty(project) ? project.filter((p) => p.companyId === value) : []
    );
    form.setFieldValue("projectId", undefined);
    form.setFieldValue("wardId", undefined);
  };

  return (
    <Modal
      title="Download Bulk QR Codes with Logo"
      open={visible}
      onOk={handleDownload}
      onCancel={handleCancel}
      okText="Download"
      cancelText="Cancel"
      confirmLoading={downloadState.isLoading}
      width={650}
      okButtonProps={{ disabled: downloadState.isLoading }}
      cancelButtonProps={{ disabled: downloadState.isLoading }}
    >
      <Spin spinning={downloadState.isLoading} tip="Generating PDF...">
        <Form layout="vertical" form={form}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="companyId"
                label={<strong>Company</strong>}
                rules={[{ required: true, message: "Please select a company" }]}
              >
                <Select
                  placeholder="Select Company"
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  onChange={companySelected}
                >
                  {!isEmpty(company) &&
                    company.map((c) => (
                      <Select.Option key={c.id} value={c.id}>
                        {c.companyName}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="projectId"
                label={<strong>Project</strong>}
                rules={[{ required: true, message: "Please select a project" }]}
              >
                <Select
                  placeholder="Select Project"
                  allowClear
                  showSearch
                  disabled={!selectedCompany}
                  filterOption={(input, option) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {filteredProjects.map((p) => (
                    <Select.Option key={p.id} value={p.id}>
                      {p.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="wardId"
                label={<strong>Ward</strong>}
                rules={[{ required: true, message: "Please select a project" }]}
              >
                <Select
                  placeholder="Select Ward"
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {!isEmpty(ward) &&
                    ward.map((w) => (
                      <Select.Option key={w.id} value={w.id}>
                        {w.name}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {downloadState.isLoading && (
            <Row gutter={16} style={{ marginTop: "20px" }}>
              <Col span={24}>
                <Progress
                  percent={Math.floor(downloadProgress)}
                  status={downloadProgress > 0 ? "active" : "normal"}
                  format={() =>
                    `Generating PDF: ${Math.floor(downloadProgress)}%`
                  }
                />
              </Col>
            </Row>
          )}

          <Row gutter={16} style={{ marginTop: "15px" }}>
            <Col span={24}>
              <div
                style={{
                  padding: "12px",
                  backgroundColor: "#f0f2f5",
                  borderRadius: "4px",
                  fontSize: "12px",
                  color: "#666",
                }}
              >
                ℹ️ <strong>Note:</strong> QR codes will include company logo in
                the center. Each page contains one QR code with username and ID.
              </div>
            </Col>
          </Row>
        </Form>
      </Spin>
    </Modal>
  );
}

export default BulkQRDownload;
